import React, { useState, useEffect } from "react";
import { Col, Form, Input, Modal, Row, Select, notification } from "antd";
import styled from "styled-components";
import { CALL_FEATCH_API } from "common/API";
import { currencyCodeMap, currencySymbolMap } from "common/Constants";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const NON_CURRENCY_FIELDS = new Set([
    "id", "date", "updated_currency", "updated_at",
]);

const toLabel = (key) =>
    key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const buildCurrencyOptions = (currencyList) => {
    return Object.keys(currencyList || {})
        .filter((key) => !NON_CURRENCY_FIELDS.has(key))
        .map((key) => ({
            value: key,
            label: toLabel(key),
        }));
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const StyledModal = styled(Modal)`
    .ant-modal-header {
        border-bottom: none;
        padding-top: 20px;
        padding-bottom: 20px;
    }
    .ant-modal-title {
        font-weight: 700;
        font-size: 1.1rem;
    }
    .ant-modal-body {
        padding-top: 0;
    }
    .ant-modal-footer {
        display: none;
    }
    .ant-form-item-label label {
        font-weight: 500;
    }
`;

const ModalBody = styled.div`
    padding: 0 2% 2%;

    .footer-btns {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 8px;
    }

    .btn-cancel {
        border-radius: 8px !important;
        border: 1px solid #1a1a1a !important;
        background: #fff !important;
        color: #1a1a1a !important;
        font-weight: 500 !important;
        height: 36px !important;
        padding: 0 20px !important;
        cursor: pointer;
    }

    .btn-save {
        border-radius: 8px !important;
        background: #00685F!important;
        border: #00685F !important;
        color: rgb(255, 255, 255) !important;
        font-weight: 600 !important;
        height: 36px !important;
        padding: 0 24px !important;
        cursor: pointer;
    }

    .btn-save:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const AddSubcontractorModal = ({ open, onClose, onAdd, currencyList }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [subcontractors, setSubcontractors] = useState([]);
    const [subLoading, setSubLoading] = useState(false);
    const [selectedCurrencyRate, setSelectedCurrencyRate] = useState(null); // ✅ tracks live rate

    const currencyOptions = buildCurrencyOptions(currencyList);

    useEffect(() => {
        if (!open) return;
        const fetchSubcontractors = async () => {
            setSubLoading(true);
            try {
                const res = await CALL_FEATCH_API("insyts-billing-entity/getsubcontractors", "GET");
                setSubcontractors(res?.response?.data || []);
            } catch (err) {
                console.error("[fetchSubcontractors]", err);
                setSubcontractors([]);
            } finally {
                setSubLoading(false);
            }
        };
        fetchSubcontractors();
    }, [open]);

    const handleClose = () => {
        form.resetFields();
        setSelectedCurrencyRate(null); // ✅ reset rate on close
        onClose();
    };

    // ✅ When user picks a currency, look up its rate from currencyList
    const handleCurrencyChange = (selectedKey) => {
        const rate = currencyList?.[selectedKey] ?? null;
        setSelectedCurrencyRate(rate);
    };
    const onFinish = async (values) => {
        setLoading(true);
        try {
            await new Promise((r) => setTimeout(r, 400));

            const selectedSub = subcontractors.find((s) => s.id === values.subcontractor);

            const selectedCurrencyOption = currencyOptions.find((opt) => opt.value === values.currency);
            const currencyCode = selectedCurrencyOption?.value ?? values.currency;

            const newSub = {
                key: Date.now().toString(),
                sub: selectedSub?.subcontractor ?? values.subcontractor,
                description: values.description,
                currency: selectedCurrencyOption?.label,
                currencycode: currencyCodeMap[currencyCode],
                currencyrate: selectedCurrencyRate,
                amount: `${currencySymbolMap[currencyCodeMap[currencyCode]]} ${Number(values.amount)}`,
                remark: values.remark,
                refno: values.refno,
            };
            notification.success({ message: "Subcontractor added successfully!" });
            onAdd(newSub);
            handleClose();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <StyledModal
            title="Add Subcontractor"
            open={open}
            onCancel={handleClose}
            width={800}
            footer={null}
            destroyOnClose
        >
            <ModalBody>
                <Form form={form} layout="vertical" onFinish={onFinish} colon={false}>
                    <Row gutter={24}>
                        {/* Subcontractor */}
                        <Col span={12}>
                            <Form.Item
                                label="Subcontractor"
                                name="subcontractor"
                                rules={[{ required: true, message: "Subcontractor is required." }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Select subcontractor"
                                    loading={subLoading}
                                    filterOption={(input, option) =>
                                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={subcontractors.map((s) => ({
                                        label: s.subcontractor,
                                        value: s.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>

                        {/* Description */}
                        <Col span={12}>
                            <Form.Item
                                label="Description"
                                name="description"
                                rules={[{ required: true, message: "Description is required." }]}
                            >
                                <Input placeholder="Enter description" />
                            </Form.Item>
                        </Col>

                        {/* Currency */}
                        <Col span={12}>
                            <Form.Item
                                label="Currency"
                                name="currency"
                                rules={[{ required: true, message: "Currency is required." }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Select currency"
                                    options={currencyOptions}
                                    onChange={handleCurrencyChange} // ✅ captures rate on selection
                                    filterOption={(input, option) =>
                                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>

                        {/* Amount */}
                        <Col span={12}>
                            <Form.Item
                                label="Amount"
                                name="amount"
                                rules={[
                                    { required: true, message: "Amount is required." },
                                    {
                                        validator(_, value) {
                                            if (!value) return Promise.resolve();
                                            if (isNaN(Number(value)) || Number(value) <= 0)
                                                return Promise.reject("Enter a valid positive amount.");
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    type="number"
                                    placeholder="Enter amount"
                                    onKeyDown={(e) => {
                                        if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Reference No */}
                        <Col span={12}>
                            <Form.Item
                                label="Reference No"
                                name="refno"
                                rules={[{ required: true, message: "Reference No is required." }]}
                            >
                                <Input placeholder="Enter reference number" />
                            </Form.Item>
                        </Col>

                        {/* Remark */}
                        <Col span={12}>
                            <Form.Item
                                label="Remark"
                                name="remark"
                                rules={[{ required: true, message: "Remark is required." }]}
                            >
                                <Input placeholder="Enter remark" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="footer-btns">
                        <button type="button" className="btn-cancel" onClick={handleClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-save" disabled={loading}>
                            {loading ? "Saving…" : "Save"}
                        </button>
                    </div>
                </Form>
            </ModalBody>
        </StyledModal>
    );
};

export default AddSubcontractorModal;